import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import cartService from '../../db/cart-service';
import subscriptionService from '../../services/subscription-service';
import './MobileBottomNav.scss';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [hasSubscriptions, setHasSubscriptions] = useState(false);
  
  const customerId = sessionStorage.getItem('customerId');
  const isAuthenticated = sessionStorage.getItem('isUserAuthenticated') === 'true';

  // Check subscriptions
  const fetchCartCount = async () => {
    if (customerId && isAuthenticated) {
      try {
        const cart = await cartService.getCart(customerId);
        const count = cart.reduce((total, item) => total + item.quantity, 0);
        setCartCount(count);
      } catch (err) {
        console.error('Error fetching cart:', err);
      }
    } else {
      setCartCount(0);
    }
  };

  // Check subscriptions
  const checkSubscriptions = async () => {
    if (customerId && isAuthenticated) {
      try {
        const userHasSubscriptions = await subscriptionService.hasUserSubscriptions(customerId);
        setHasSubscriptions(userHasSubscriptions);
      } catch (error) {
        setHasSubscriptions(false);
      }
    } else {
      setHasSubscriptions(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && customerId) {
      fetchCartCount();
      checkSubscriptions();
    }
  }, [customerId, isAuthenticated]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => fetchCartCount();
    const handleSubscriptionUpdate = () => checkSubscriptions();

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('refreshSubscriptions', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('refreshSubscriptions', handleSubscriptionUpdate);
    };
  }, [isAuthenticated, customerId]);

  const handleNavigation = (path, requiresAuth = false) => {
    if (requiresAuth && !isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(path);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="mobile-bottom-nav">
      <button 
        className={`nav-item ${isActive('/') ? 'active' : ''}`}
        onClick={() => handleNavigation('/')}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
        <span>Home</span>
      </button>

      <button 
        className={`nav-item ${isActive('/cart') ? 'active' : ''}`}
        onClick={() => handleNavigation('/cart', true)}
      >
        <div className="cart-icon-container">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
          {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
        </div>
        <span>Cart</span>
      </button>

      <button 
        className={`nav-item ${isActive('/orders') ? 'active' : ''}`}
        onClick={() => handleNavigation('/orders', true)}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
        <span>Orders</span>
      </button>

      <button 
        className={`nav-item ${isActive('/plants') ? 'active' : ''}`}
        onClick={() => handleNavigation('/plants')}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2-8 2 0-4-4-4-4-4 0 4 2.5 6.5 2.5 6.5C9 8 7 10 7 10c0-4 4-6 4-6C7 4 5 8 5 8c0-2 2-4 2-4C3 6 3 12 3 12c0-2 1-4 1-4C2 12 2 16 2 16c2-2 4-2 4-2-2 2-2 4-2 4 2-1 4-1 4-1-1 1-1 3-1 3 3-3 5-3 5-3-1 2 0 4 0 4 2-4 5-4 5-4z"/>
        </svg>
        <span>Library</span>
      </button>

      <button 
        className={`nav-item ${isActive('/subscriptions') ? 'active' : ''}`}
        onClick={() => handleNavigation('/subscriptions')}
      >
        <div className="subscription-icon-container">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          {hasSubscriptions && <span className="subscription-dot"></span>}
        </div>
        <span>Plans</span>
      </button>

      <button 
        className={`nav-item ${isActive('/customer') || isActive('/login') ? 'active' : ''}`}
        onClick={() => handleNavigation(isAuthenticated ? '/customer' : '/login')}
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
        <span>{isAuthenticated ? 'Profile' : 'Login'}</span>
      </button>
    </div>
  );
};

export default MobileBottomNav;